import React from 'react';
import styled from 'styled-components';
import type { WeeklyOrder } from './delivery_start';

// 인보이스 출력용 AUD 홀세일 가격입니다. 5% 인상분은 센트 미만을 버려 반영했습니다.
// Cookie Choux 두 종류는 인상 대상에서 제외합니다.
export const BUTTERED_WHOLESALE_PRICES = {
  'bg-1': 3.15, // Plain Bagel
  'bg-2': 6.93, // Ham & Cheese Bagel
  'bg-3': 4.72, // Plain Bagel with Cream Cheese
  'sb-1': 4.41, // Plain Salt Bread
  'sb-2': 5.77, // Beton Salt Bread
  'sb-3': 5.67, // Garlic Cream Cheese
  'sb-4': 6.30, // Corn Cheese
  'sb-7': 6.93, // Teriyaki Chicken
  'sb-8': 6.93, // Buldak Chicken
  'sb-18': 6.30, // Cinnamon Apple
  'sb-14': 6.93, // Creme Brulee
  'sb-9': 5.67, // Vanilla Cream
  'sb-19': 6.30, // Ang Butter
  'sb-10': 5.04, // Coffee
  'sb-12': 6.30, // Matcha Cream
  'sb-11': 6.93, // Nutella
  'wh-strawberry-wrap': 7.14, // Strawberry Wrap
  'wh-chocolate-potato-crunch': 6.93, // Chocolate Potato Crunch
  'sb-16': 5.67, // Lemon Salt
  'sb-5': 7.14, // Prawn Croquette Potato
  'sb-13': 6.30, // Purple Sweet Potato
  'cr-2': 6.30, // Almond Croissant
  'cr-1': 5.04, // Plain Croissant
  'cr-3': 6.30, // Pain au Chocolat
  'wh-candied-croissant': 5.67, // Candied Croissant
  'wh-duchokooo': 6.51, // Duchokooo
  'sw-1': 2.62, // Almond Cookie
  'sw-2': 7.35, // Butter Rice Cake (5 pcs)
  'sw-3': 6.60, // Cookie Choux (Strawberry)
  'sw-4': 6.60, // Cookie Choux (Chocolate)
  'wh-butter-rice-cake-10': 13.12, // Butter Rice Cake (10 pcs)
  'sc-1': 5.04, // Plain Scone
  'sc-2': 5.67, // Earl Grey Scone
  'sc-4': 5.98, // Blueberry Scone
  'sc-3': 5.98, // Raspberry Scone
} as const;

// 케이크 가격은 조각당 가격이 아니라 아래 구성 전체의 홀세일 가격입니다.
export const BUTTERED_CAKE_WHOLESALE_PRICES = {
  'fz-1': { pieces: 8, wholesalePriceAud: 63 }, // Frozen Baked Cheesecake
  'fz-2': { pieces: 8, wholesalePriceAud: 67.20 }, // Frozen Yuzu Cheesecake
  'fz-3': { pieces: 8, wholesalePriceAud: 71.40 }, // Frozen Chestnut Cheesecake
  'fz-4': { pieces: 8, wholesalePriceAud: 69.30 }, // Frozen Tiramisu
  'fc-1': { pieces: 6, wholesalePriceAud: 66.15 }, // Fresh Sweet Corn Cake
  'fc-4': { pieces: 6, wholesalePriceAud: 68.25 }, // Fresh Vanilla Fruit Cake
  'fc-3': { pieces: 6, wholesalePriceAud: 69.30 }, // Fresh Chocolate Fruit Cake
  'fc-6': { pieces: 6, wholesalePriceAud: 68.25 }, // Fresh Blueberry Yogurt Tart
} as const;

// ATO Detailed Food List: unfilled bagels (17) and plain bread/rolls (114)
// are GST-free; croissants (261) and scones (687) are taxable even when plain.
// Assumes plain bread supplied for resale, not dine-in/hot takeaway.
// https://www.ato.gov.au/law/view/document?DocID=GII/GSTIIFL1/NAT/ATO/00001
// Other specialty products require recipe/supply review before adding exemptions.
const GST_FREE_PRODUCT_IDS = new Set(['bg-1', 'sb-1']);

type InvoiceLine = {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number | null;
  amount: number | null;
  gstFree: boolean;
};

const getWholesalePrice = (itemId: string): number | null => {
  if (itemId in BUTTERED_WHOLESALE_PRICES) {
    return BUTTERED_WHOLESALE_PRICES[itemId as keyof typeof BUTTERED_WHOLESALE_PRICES];
  }

  if (itemId in BUTTERED_CAKE_WHOLESALE_PRICES) {
    return BUTTERED_CAKE_WHOLESALE_PRICES[itemId as keyof typeof BUTTERED_CAKE_WHOLESALE_PRICES].wholesalePriceAud;
  }

  return null;
};

export const buildInvoiceLines = (order: WeeklyOrder): InvoiceLine[] => {
  const totals = new Map<string, { name: string; quantity: number }>();

  Object.values(order.dailyItems || {}).forEach(items => {
    items.forEach(item => {
      const current = totals.get(item.id);
      totals.set(item.id, {
        name: item.name,
        quantity: (current?.quantity || 0) + item.quantity,
      });
    });
  });

  return Array.from(totals.entries())
    .map(([id, item]) => {
      const unitPrice = getWholesalePrice(id);
      return {
        id,
        name: item.name,
        quantity: item.quantity,
        unitPrice,
        gstFree: GST_FREE_PRODUCT_IDS.has(id),
        amount: unitPrice === null ? null : unitPrice * item.quantity,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const calculateInvoiceTotals = (lines: InvoiceLine[]) => {
  const subtotal = Math.round(
    lines.reduce((sum, line) => sum + (line.amount ?? 0), 0) * 100,
  ) / 100;
  const taxableSubtotal = lines.reduce(
    (sum, line) => sum + (line.gstFree ? 0 : (line.amount ?? 0)),
    0,
  );
  const gst = Math.round(taxableSubtotal * 0.1 * 100) / 100;
  const totalIncludingGst = Math.round((subtotal + gst) * 100) / 100;
  return { subtotal, gst, totalIncludingGst };
};

const formatAud = (amount: number) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(amount);

const Section = styled.section`
  margin-top: 16px;
  text-align: left;

  @media print {
    display: none;
    margin: 0;
    padding: 0;
    border: 0;

    body.print-invoices & {
      display: block;
    }
  }
`;

const InvoiceGrid = styled.div`
  display: none;
  gap: 24px;

  @media print {
    body.print-invoices & {
      display: block;
    }
  }
`;

const InvoiceCard = styled.article`
  padding: 28px;
  border: 1px solid #dedede;
  border-radius: 16px;
  background: #fff;

  @media (max-width: 640px) {
    padding: 20px 14px;
    overflow-x: auto;
  }

  @media print {
    box-sizing: border-box;
    padding: 4mm 6mm;
    overflow: visible;
    border: 0;
    border-radius: 0;
    page-break-after: always;

    &:last-child {
      page-break-after: auto;
    }
  }
`;

const InvoiceHeader = styled.header`
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding-bottom: 18px;
  border-bottom: 3px solid #1a1a1a;

  @media print {
    padding-bottom: 10px;
  }
`;

const Brand = styled.div`
  font-size: 24px;
  font-weight: 900;
  letter-spacing: 0.08em;
`;

const InvoiceLabel = styled.div`
  text-align: right;
  font-size: 20px;
  font-weight: 800;
`;

const InvoiceMeta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin: 20px 0 24px;
  font-size: 14px;

  @media print {
    margin: 12px 0;
    font-size: 12px;
  }
`;

const MetaLabel = styled.div`
  margin-bottom: 5px;
  color: #777;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Table = styled.table`
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    padding: 11px 10px;
    border-bottom: 1px solid #e8e8e8;
  }

  th {
    background: #f7f7f7;
    color: #555;
    font-size: 11px;
    letter-spacing: 0.05em;
    text-align: left;
    text-transform: uppercase;
  }

  th:nth-child(n + 2),
  td:nth-child(n + 2) {
    text-align: right;
  }

  @media print {
    min-width: 0;
    font-size: 11px;

    th,
    td {
      padding: 5px 8px;
    }

    tr {
      break-inside: avoid;
    }
  }
`;

const Pending = styled.span`
  color: #a06400;
  font-size: 12px;
  font-weight: 700;
`;

const InvoiceFooter = styled.footer`
  display: flex;
  justify-content: flex-end;
  margin-top: 22px;

  @media print {
    margin-top: 12px;
    break-inside: avoid;
    page-break-inside: avoid;
  }
`;

const TotalBox = styled.div`
  width: 280px;
  padding-top: 14px;
  border-top: 2px solid #1a1a1a;

  @media print {
    width: 340px;
    max-width: 100%;
    padding-top: 8px;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-top: 8px;
  padding-top: 10px;
  border-top: 1px solid #d8d8d8;
  font-size: 18px;
  font-weight: 800;

  @media print {
    font-size: 15px;

    span:last-child {
      white-space: nowrap;
    }
  }
`;

const CalculationRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 7px;
  color: #555;
  font-size: 12px;
`;

const TaxNote = styled.p`
  margin: 8px 0 0;
  color: #777;
  font-size: 10px;
  line-height: 1.4;
`;

const PendingNote = styled.p`
  margin: 10px 0 0;
  color: #8a5a00;
  font-size: 11px;
  line-height: 1.4;
`;

type DeliveryInvoiceSectionProps = {
  orders: WeeklyOrder[];
  weekStart: string;
  weekEnd: string;
};

const DeliveryInvoiceSection: React.FC<DeliveryInvoiceSectionProps> = ({
  orders,
  weekStart,
  weekEnd,
}) => (
  <Section className="invoice-print-section">
    <InvoiceGrid>
      {orders.map(order => {
        const lines = buildInvoiceLines(order);
        const { subtotal, gst, totalIncludingGst } = calculateInvoiceTotals(lines);
        const pendingCount = lines.filter(line => line.unitPrice === null).length;

        return (
          <InvoiceCard key={`invoice-${order.branchId}`}>
            <InvoiceHeader>
              <Brand>BUTTERED</Brand>
              <InvoiceLabel>WEEKLY INVOICE</InvoiceLabel>
            </InvoiceHeader>

            <InvoiceMeta>
              <div>
                <MetaLabel>Bill To</MetaLabel>
                <strong>{order.branchName}</strong>
              </div>
              <div style={{ textAlign: 'right' }}>
                <MetaLabel>Order Period</MetaLabel>
                <strong>{weekStart} – {weekEnd}</strong>
              </div>
            </InvoiceMeta>

            <Table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Weekly Qty</th>
                  <th>Unit Price</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(line => (
                  <tr key={line.id}>
                    <td>{line.name}{line.gstFree && ' (GST-free)'}</td>
                    <td>{line.quantity}</td>
                    <td>{line.unitPrice === null ? <Pending>Pending</Pending> : formatAud(line.unitPrice)}</td>
                    <td>{line.amount === null ? '—' : formatAud(line.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <InvoiceFooter>
              <TotalBox>
                <CalculationRow>
                  <span>{pendingCount > 0 ? 'Priced Subtotal' : 'Subtotal'}</span>
                  <span>{formatAud(subtotal)}</span>
                </CalculationRow>
                <CalculationRow>
                  <span>GST (10%)</span>
                  <span>{formatAud(gst)}</span>
                </CalculationRow>
                <TotalRow>
                  <span>{pendingCount > 0 ? 'Priced Total incl. GST' : 'Total incl. GST'}</span>
                  <span>{formatAud(totalIncludingGst)}</span>
                </TotalRow>
                <TaxNote>10% GST applies only to taxable products. Products marked GST-free are excluded from GST.</TaxNote>
                {pendingCount > 0 && (
                  <PendingNote>
                    {pendingCount} product price{pendingCount === 1 ? ' is' : 's are'} pending and excluded from the total.
                  </PendingNote>
                )}
              </TotalBox>
            </InvoiceFooter>
          </InvoiceCard>
        );
      })}
    </InvoiceGrid>
  </Section>
);

export default DeliveryInvoiceSection;
